/**
 * WebSocket client + React hooks for live panel feeds.
 *
 * Server-side routes (apps/api/src/routes/ws.ts):
 *   /ws/jobs       — 1s delta stream of Job rows that changed state/progress
 *   /ws/dashboard  — 5s push of dashboard KPI counts
 *
 * Auth: the JWT access token must travel in the URL query string because
 * browsers can't set Authorization headers on a WebSocket upgrade.
 *
 * Reconnect: on close we wait an exponential-backoff interval (1s → 2s → 4s
 * → max 30s) and try again, capped to avoid hammering the panel after a long
 * outage. We expose `connection` state so the UI can show a badge.
 */

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/stores/auth";

export type WsState = "connecting" | "open" | "closed" | "error";

export type WsHookResult<T> = {
  state: WsState;
  lastMessage: T | null;
  lastReceivedAt: number | null;
};

function buildUrl(path: string, token: string | null): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  const tokenParam = token ? `?token=${encodeURIComponent(token)}` : "";
  return `${proto}//${window.location.host}${path}${tokenParam}`;
}

export function useWs<T>(path: string): WsHookResult<T> {
  const accessToken = useAuth((s) => s.session?.accessToken ?? null);
  const [state, setState] = useState<WsState>("connecting");
  const [lastMessage, setLastMessage] = useState<T | null>(null);
  const [lastReceivedAt, setLastReceivedAt] = useState<number | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectMsRef = useRef(1000);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!accessToken) {
      setState("closed");
      return;
    }
    stoppedRef.current = false;

    const connect = () => {
      if (stoppedRef.current) return;
      setState("connecting");
      const ws = new WebSocket(buildUrl(path, accessToken));
      wsRef.current = ws;

      ws.addEventListener("open", () => {
        setState("open");
        reconnectMsRef.current = 1000; // reset backoff on a successful open
      });
      ws.addEventListener("message", (ev) => {
        try {
          const data = JSON.parse(ev.data) as T;
          setLastMessage(data);
          setLastReceivedAt(Date.now());
        } catch {
          /* drop unparseable frames */
        }
      });
      ws.addEventListener("close", () => {
        setState("closed");
        if (stoppedRef.current) return;
        const wait = reconnectMsRef.current;
        reconnectMsRef.current = Math.min(reconnectMsRef.current * 2, 30_000);
        setTimeout(connect, wait);
      });
      ws.addEventListener("error", () => {
        setState("error");
        // The 'close' handler will fire next and trigger reconnect.
      });
    };

    connect();
    return () => {
      stoppedRef.current = true;
      try {
        wsRef.current?.close();
      } catch {
        /* ignore */
      }
    };
  }, [path, accessToken]);

  return { state, lastMessage, lastReceivedAt };
}

// ─────────────────────── Typed wrappers per stream ───────────────────────

export type JobsBatch = {
  type: "batch";
  events: Array<{
    type: "job";
    id: string;
    serverName: string;
    cpanelUser: string | null;
    kind: string;
    state: string;
    progressPct: number;
    bytesTransferred: number;
    startedAt: string | null;
    finishedAt: string | null;
    errorMsg: string | null;
  }>;
};

export type DashboardPush = {
  type: "dashboard";
  timestamp: string;
  counts: {
    serverCount: number;
    accountCount: number;
    runningJobs: number;
    failed24h: number;
  };
};

export function useJobsWs() {
  return useWs<JobsBatch>("/ws/jobs");
}

export function useDashboardWs() {
  return useWs<DashboardPush>("/ws/dashboard");
}
